import { useCallback, useEffect, useMemo, useState } from 'react';
import { listServices, listSystems } from '../services/service-config.service';

/**
 * @typedef {Object} ServiceRow
 * @property {number} serviceId
 * @property {string} serviceName
 * @property {string|null} displayName
 * @property {number} systemKey
 * @property {string|null} systemName
 * @property {string|null} cmsKey
 * @property {string|null} mainUrl
 * @property {boolean} active
 */

const matchesQuery = (service, query) => {
  const haystack = [service.serviceName, service.displayName, service.cmsKey, service.systemName]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return haystack.includes(query);
};

export const useServiceConfigurationHook = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [services, setServices] = useState(/** @type {ServiceRow[]} */ ([]));
  const [systems, setSystems] = useState(/** @type {Array<{ systemId: number, systemName: string|null }>} */ ([]));
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingService, setEditingService] = useState(/** @type {ServiceRow | null} */ (null));
  const [toast, setToast] = useState(/** @type {{ type: string, message: string } | null} */ (null));

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [serviceRows, systemRows] = await Promise.all([listServices(), listSystems()]);
      setServices(Array.isArray(serviceRows) ? serviceRows : []);
      setSystems(Array.isArray(systemRows) ? systemRows : []);
    } catch (requestError) {
      console.error('ServiceConfiguration: failed to load services', requestError);
      setError('Failed to load services');
      setServices([]);
      setSystems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const openCreate = useCallback(() => {
    setEditingService(null);
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((service) => {
    setEditingService(service);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingService(null);
  }, []);

  const handleSaved = useCallback(
    (message) => {
      setToast({ message: message || 'Saved', type: 'success' });
      closeModal();
      reload();
    },
    [closeModal, reload],
  );

  const handleSystemCreated = useCallback((system) => {
    setSystems((prev) => [...prev, system].sort((a, b) => String(a.systemName).localeCompare(String(b.systemName))));
  }, []);

  const handleCloseToast = useCallback(() => setToast(null), []);

  const filteredServices = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (query === '') {
      return services;
    }

    return services.filter((service) => matchesQuery(service, query));
  }, [search, services]);

  return {
    closeModal,
    editingService,
    error,
    filteredServices,
    handleCloseToast,
    handleSaved,
    handleSystemCreated,
    loading,
    modalOpen,
    openCreate,
    openEdit,
    search,
    setSearch,
    systems,
    toast,
  };
};
